import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { GAME_CONFIG } from '@/shared/config/constants';
import type { CurrencyCode } from '@/shared/lib/currency';

import { JettonHistoryEntry, JettonRound, JettonState, JettonStatus } from './types';

const emptyRound: JettonRound = {
  id: '',
  index: 0,
  status: JettonStatus.WAITING,
  serverSeedHash: null,
  startTimeMs: null,
  startsInMs: null,
  crashMultiplier: null,
  totalBet: 0,
  totalsByCurrency: {},
  bets: [],
};

const initialState: JettonState = {
  currentRound: emptyRound,
  history: [],
  myCashout: null,
  waitingEndsAtMs: null,
  selectedCurrency: 'STARS',
  autoplay: {
    isActive: false,
    betAmount: GAME_CONFIG.JETTON.MIN_BET,
    cashoutMultiplier: 2.0,
    numberOfGames: 10,
    gamesPlayed: 0,
  },
};

// Compute client-side deadline from server-sent remaining ms (countdown phase only)
function calcDeadline(round: JettonRound): number | null {
  if (round.status === 'countdown' && round.startsInMs != null) {
    return Date.now() + round.startsInMs;
  }
  return null;
}

const jettonSlice = createSlice({
  name: 'jetton',
  initialState,
  reducers: {
    // Full round sync (from API on init or WS)
    setRound(state, action: PayloadAction<JettonRound>) {
      state.currentRound = action.payload;
      state.waitingEndsAtMs = calcDeadline(action.payload);
      if (action.payload.history?.length) state.history = action.payload.history;
    },

    // New round — infinite wait, no timer
    wsWaiting(state, action: PayloadAction<JettonRound>) {
      state.currentRound = action.payload;
      state.myCashout = null;
      state.waitingEndsAtMs = null;
      state.history = action.payload.history ?? [];
    },

    // First bet placed — 10 s countdown begins
    wsCountdown(state, action: PayloadAction<JettonRound>) {
      state.currentRound = action.payload;
      state.waitingEndsAtMs = calcDeadline(action.payload);
    },

    // Flying phase started — includes startTimeMs
    wsRoundStart(state, action: PayloadAction<JettonRound>) {
      state.currentRound = { ...action.payload, crashMultiplier: null };
      state.waitingEndsAtMs = null;
      if (action.payload.history?.length) state.history = action.payload.history;
    },

    // Someone placed/cancelled a bet — update bets list + totalBet
    wsNewBet(state, action: PayloadAction<JettonRound>) {
      state.currentRound.bets = action.payload.bets;
      state.currentRound.totalBet = action.payload.totalBet;
      state.currentRound.totalsByCurrency = action.payload.totalsByCurrency;
    },

    // Someone cashed out — update their entry
    wsCashoutEvent(
      state,
      action: PayloadAction<{ userId: string; multiplier: number; payout: number }>,
    ) {
      const { userId, multiplier } = action.payload;
      const bet = state.currentRound.bets.find((b) => b.userId === userId);
      if (bet) {
        bet.cashoutMultiplier = multiplier;
        bet.isWinner = true;
      }
    },

    // Round crashed
    wsCrash(
      state,
      action: PayloadAction<{ crashMultiplier: number; history?: JettonHistoryEntry[] }>,
    ) {
      state.currentRound.status = JettonStatus.CRASHED;
      state.currentRound.crashMultiplier = action.payload.crashMultiplier;
      state.waitingEndsAtMs = null;
      if (action.payload.history) {
        state.history = action.payload.history;
      }
    },

    // User's own cashout confirmed by API
    setMyCashout(state, action: PayloadAction<{ multiplier: number; payout: number }>) {
      state.myCashout = action.payload;
    },

    setSelectedCurrency(state, action: PayloadAction<CurrencyCode>) {
      state.selectedCurrency = action.payload;
    },

    // Autoplay
    startAutoplay(
      state,
      action: PayloadAction<{
        betAmount: number;
        cashoutMultiplier: number;
        numberOfGames: number;
      }>,
    ) {
      state.autoplay.isActive = true;
      state.autoplay.betAmount = action.payload.betAmount;
      state.autoplay.cashoutMultiplier = action.payload.cashoutMultiplier;
      state.autoplay.numberOfGames = action.payload.numberOfGames;
      state.autoplay.gamesPlayed = 0;
    },
    recordAutoplayGame(state) {
      state.autoplay.gamesPlayed += 1;
    },
    stopAutoplay(state) {
      state.autoplay.isActive = false;
      state.autoplay.gamesPlayed = 0;
    },

    // User actions (sagas listen)
    placeBet(
      _state,
      _action: PayloadAction<{ amount: number; currency: CurrencyCode; autoCashoutMultiplier?: number }>,
    ) {},
    requestCashout(_state) {},
    cancelBet(_state) {},
  },
});

export const jettonActions = jettonSlice.actions;
export const jettonReducer = jettonSlice.reducer;
